import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpStatus } from '../../common/enums/HttpStatus';
import { IContent } from '../../common/interfaces/Content.interface';
import { IContentGroupedByContextResponse } from './../../common/interfaces/ContentGroupedByContextResponse.interface';
import { createBaseQuery } from './utils/createBaseQuery';

export const contentApi = createApi({
  reducerPath: 'contentApi',
  baseQuery: createBaseQuery('/contents'),
  tagTypes: ['Content'],
  endpoints: (builder) => ({
    fetchActiveContent: builder.query<
      { code: HttpStatus; data: IContentGroupedByContextResponse[] },
      { page: number; size: number }
    >({
      query: ({ page, size }) => ({
        url: '/list',
        params: {
          page,
          size,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Content' as const, id: 'LIST' },
              ...result.data.map(({ _id }) => ({
                type: 'Content' as const,
                _id,
              })),
            ]
          : [{ type: 'Content', id: 'LIST' }],
    }),

    fetchContentById: builder.query<IContent, string>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: 'Content', id }],
    }),

    createContent: builder.mutation<IContent, Partial<IContent>>({
      query: (newContent) => ({
        url: '',
        method: 'POST',
        body: newContent,
      }),
      invalidatesTags: [{ type: 'Content', id: 'LIST' }],
    }),

    updateContent: builder.mutation<
      IContent,
      { id: string; updatedContent: Partial<IContent> }
    >({
      query: ({ id, updatedContent }) => ({
        url: `/${id}`,
        method: 'PATCH',
        body: updatedContent,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Content', id },
        { type: 'Content', id: 'LIST' },
      ],
    }),

    deleteContent: builder.mutation<{ success: boolean; id: string }, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Content', id },
        { type: 'Content', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useCreateContentMutation,
  useFetchActiveContentQuery,
  useFetchContentByIdQuery,
  useUpdateContentMutation,
  useDeleteContentMutation,
} = contentApi;
